import { Component, OnInit } from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { StructureService } from "../services/structure/structure.service";
import { StructureFilter, StructureInstance, StructureStatus } from "@models/structure.model";
import { MatDialog } from "@angular/material/dialog";
import { GenericDialogComponent } from "../shared/generic-dialog/generic-dialog.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { debounceTime, distinctUntilChanged, finalize } from "rxjs";
import { SnackBarConfig } from "../constants/snackbar.constants";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { environment } from "~/src/environments/environment";
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";


@Component({
    selector: "app-my-structures",
    templateUrl: "./my-structures.component.html",
    styleUrls: ["./my-structures.component.scss"],
    standalone: false,
})
export class MyStructuresComponent implements OnInit {
    public structures: { totalCount: number; count: number; rows: StructureInstance[]; status: string } = {
        totalCount: 0,
        count: 0,
        rows: [],
        status: StructureFilter.All,
    };
    public loading = false;
    private pageSize = environment.dynamicPagination.pageSize;
    public selectedStructureIds: Set<number> = new Set<number>();
    public searchQuery = "";
    public currentStatus: string = StructureFilter.All;
    public statusOptions: string[] = Object.values(StructureFilter);
    public structureFilter = StructureFilter;
    private searchTrigger$ = new Subject<string>();
    public searchControl = new FormControl("");
    public selectedStatus: string = "";

    constructor(
        private structureService: StructureService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private router: Router,
    ) { }

    ngOnInit() {
        this.fetchMyStructures();
        this.searchControl.valueChanges.subscribe((query) => {
            this.searchQuery = query?.trim() || "";
        });
        this.searchTrigger$.pipe(debounceTime(250), distinctUntilChanged()).subscribe((query) => {
            this.searchFromMyStructures(query);
        });
    }

    // Disable bulk actions until at least two structures are selected
    get isBulkActionDisabled(): boolean {
        return this.selectedStructureIds.size < 2;
    }

    private fetchMyStructures(offset = 0, searchQuery?: string, status?: string): void {
        if (offset === 0) {
            this.structures = { totalCount: 0, count: 0, rows: [], status: this.currentStatus };
        }
        this.loading = true;
        this.structureService
            .getSavedStructures(offset, this.pageSize, searchQuery, status)
            .pipe(
                finalize(() => {
                    this.loading = false;
                })
            )
            .subscribe({
                next: response => {
                    this.structures = {
                        totalCount: response.totalCount,
                        count: response.count,
                        rows: [...this.structures.rows, ...response.rows],
                        status: this.currentStatus
                    };
                }
            });
    }

    public onStructuresUpdated(updatedStructures: StructureInstance[]): void {
        const updatedMap = new Map(updatedStructures.map((structure) => [structure.id, structure]));
        this.structures = {
            ...this.structures,
            rows: this.structures.rows.map((structure) =>
                updatedMap.has(structure.id) ? { ...structure, ...updatedMap.get(structure.id)! } : structure,
            ),
        };
    }

    public onStructuresDelete(structureIds: Set<number>): void {
        const updatedStructures = this.structures.rows.filter((structure) => !structureIds.has(structure.id));
        this.structures = {
            ...this.structures,
            count: this.structures.count - structureIds.size,
            rows: updatedStructures,
        };
        if (!this.structures.rows.length && this.structures.count) {
            this.loadMoreMyStructures();
        }
    }

    public loadMoreMyStructures(): void {
        this.fetchMyStructures(this.structures.rows.length, this.searchQuery, this.currentStatus);
    }

    private searchFromMyStructures(query: string): void {
        this.fetchMyStructures(0, query, this.currentStatus);
    }

    public get isAllSelected(): boolean {
        return (
            this.selectedStructureIds.size ===
            this.structures.rows.filter((structure) => structure.status === StructureStatus.Saved).length
        );
    }

    public get isIndeterminate(): boolean {
        return this.selectedStructureIds.size > 0 && !this.isAllSelected;
    }

    public get showSelectAllCheckbox(): boolean {
        return this.structures.rows.filter((structure) => structure.status === StructureStatus.Saved).length > 1;
    }

    public onToggleSelectAll(event: MatCheckboxChange): void {
        if (event.checked) {
            this.structures.rows
                .filter((structure) => structure.status === StructureStatus.Saved)
                .forEach((s) => this.selectedStructureIds.add(s.id));
        } else {
            this.selectedStructureIds.clear();
        }
    }

    public filterStatus(status: string): void {
        this.currentStatus = status;
        this.fetchMyStructures(0, this.searchQuery, this.currentStatus);
    }

    public onStructureCheckboxToggled(event: { id: number; checked: boolean }): void {
        if (event.checked) {
            this.selectedStructureIds.add(event.id);
        } else {
            this.selectedStructureIds.delete(event.id);
        }
    }

    // public onSelectionChange(event: Event): void {
    //     if (!this.selectedStructureIds.size) {
    //         return;
    //     }

    //     const selectElement = event.target as HTMLSelectElement;
    //     const selectedValue = selectElement.value;

    //     switch (+selectedValue) {
    //         case 1:
    //             this.onBulkPublish(this.selectedStructureIds);
    //             break;
    //         case 2:
    //             this.onBulkDelete(this.selectedStructureIds);
    //             break;
    //         default:
    //             break;
    //     }
    // }

    public onBulkPublish(): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.MyStructuresList,
                title: this.getTranslation("genericDialogBox.bulkPublish.title"),
                content: this.getTranslation("genericDialogBox.bulkPublish.content", {
                    count: this.selectedStructureIds.size,
                }),
                firstBtn: this.getTranslation("genericDialogBox.button.publishBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.structureService.publishStructures(Array.from(this.selectedStructureIds)).subscribe({
                    next: (response) => {
                        const updatedStructures = this.structures.rows
                            .filter((structure) => this.selectedStructureIds.has(structure.id))
                            .map((structure) => ({
                                ...structure,
                                status: StructureStatus.Published,
                            }));

                        this.onStructuresUpdated(updatedStructures);
                        this.selectedStructureIds.clear();

                        this.snackBar.open(response.message, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    },
                    error: (error) => {
                        this.snackBar.open(error, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    },
                });
            }
        });
    }

    public onBulkDelete(): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.MyStructuresList,
                title: this.getTranslation("genericDialogBox.bulkDelete.title"),
                content: this.getTranslation("genericDialogBox.bulkDelete.content", {
                    count: this.selectedStructureIds.size,
                }),
                firstBtn: this.getTranslation("genericDialogBox.button.deleteBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.structureService.deleteStructures(Array.from(this.selectedStructureIds)).subscribe({
                    next: (response) => {
                        this.onStructuresDelete(this.selectedStructureIds);
                        this.selectedStructureIds.clear();
                        this.snackBar.open(response.message, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    },
                    error: (error) => {
                        this.snackBar.open(error, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    },
                });
            }
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    public onSelectAllButtonClick(): void {
        const event = {
            checked: !this.isAllSelected,
        } as MatCheckboxChange;

        this.onToggleSelectAll(event);
    }

    public triggerSearch(query: string): void {
        this.searchQuery = query;
        this.searchTrigger$.next(this.searchQuery);
    }

    public goBack() {
        this.router.navigate(["/account"]);
    }
}
